import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, Clock, Wifi } from "lucide-react";

import { Skeleton } from "../../components/Skeleton/Skeleton";
import { formatRelativeShort } from "../../lib/format";
import { getProjectOnline, ProjectOnline } from "./api";
import { useNow } from "./wipe";
import styles from "./OnlineBand.module.css";

const NUM = new Intl.NumberFormat("en-US");

/**
 * What the caption says while there is no payload to describe.
 *
 * The freshness clause is a claim about a payload — while one is in flight
 * there is nothing that was taken at any time — so it joins the sentence only
 * when the answer has. The rest is true of the mechanism whatever the request
 * is doing, and saying it early is what stops the line appearing from nowhere
 * under a band that has just finished loading.
 */
const CAPTION_BASE = "Presence · lasts 60 seconds without a heartbeat";

/**
 * How often the caption re-reads the clock. The band asks once and does not
 * refetch, so nothing else would ever re-render it, and a tab left open would
 * go on saying `taken just now` about a snapshot from an hour ago.
 *
 * Half the smallest unit the sentence can print: `formatRelativeShort` changes
 * at 60 seconds and then once a minute, so a 30-second tick keeps the age it
 * shows within half a step of the truth without rendering per second.
 */
const CAPTION_TICK_MS = 30_000;

/**
 * The caption for a snapshot taken at `generatedAt` — the mockup's sentence
 * (`Присутствие · снято только что · держится 60 секунд без heartbeat`) with
 * the middle clause read off the payload instead of asserted.
 *
 * A fresh snapshot still reads «taken just now», which is what the mockup
 * draws and what the band shows for the first minute of every page load; an
 * older one says how old.
 *
 * The age is `formatRelativeShort`, not one more implementation of relative
 * time and not the verbose `formatRelativeLong` the guild columns use. Both
 * say «just now» under a minute, which is the word the mockup draws; what
 * decides between them is everything after that minute. This is an 11px line
 * of clauses divided by «·», so it wants «3h ago» rather than «3 hours ago» —
 * and lib/format.ts is where the panel keeps the formatter that is nobody
 * feature's in particular.
 *
 * An unreadable `generated_at` drops the clause rather than printing the "—"
 * that formatter returns for it: `taken — ago` is worse than not saying when.
 */
function captionFor(generatedAt: string): string {
  const age = formatRelativeShort(generatedAt);
  if (age === "—") return CAPTION_BASE;
  return `Presence · taken ${age} · lasts 60 seconds without a heartbeat`;
}

interface OnlineBandProps {
  projectId: string;
  /**
   * Why this band is not counting, when it is not — the same two words
   * AnalyticsCards takes, decided once by the page and handed to both.
   *
   * `"wiping"` shows the band without figures: presence is one of the things a
   * wipe deletes, and a live count of a project being emptied is a number that
   * was never true.
   *
   * `"unknown"` is the page still finding out whether a wipe is running. It
   * holds the skeleton rather than asking, so the request cannot win the race
   * against the answer.
   */
  paused?: "wiping" | "unknown";
  /**
   * When this project's latest wipe began, as the server timed it. Omitted
   * when it has never been wiped.
   *
   * The pause stops the band asking; it cannot stop it holding. Without this,
   * the count taken before the wipe reappears for the frames between the wipe
   * ending and the refetch landing — a live figure for players who are gone.
   * The comparison is between two of the SERVER's clocks, `generated_at`
   * against `started_at`, never the browser's.
   */
  wipeStartedAt?: string;
}

/**
 * OnlineBand — the presence strip on Project Overview, between the project card
 * and the analytics row (variant A of
 * .codelane/artifacts/t-152/online-band-players-first.html).
 *
 * The headline figure is `real`, the living players, under a caption that says
 * so: while the big number was `total` and the caption said «Online now», a
 * project running the simulation read as «412 people» when 284 of them were.
 * The sum has not gone anywhere — it stands in the split as `Total`, because
 * it is the load figure — it just no longer speaks for the players.
 *
 * A request of its own, beside `/analytics` rather than inside it: the row
 * below counts a 7-day window out of a ten-minute cache, and this counts right
 * now out of a one-second one. That is also why it carries its own freshness
 * line instead of borrowing the row's — «last 7 days · cached ~10 min» is true
 * of the three cards below and false of this figure.
 *
 * There is no sparkline and no auto-refresh. Presence has no history to draw —
 * nothing stores yesterday's count — and the number is a snapshot taken when
 * the page opened, like every other figure on this screen. What does move is
 * the line under it: a figure that never refreshes has to say how old it is,
 * or the claim outlives the snapshot.
 */
export function OnlineBand({
  projectId,
  paused,
  wipeStartedAt,
}: OnlineBandProps) {
  const q = useQuery({
    queryKey: ["project", projectId, "online"],
    queryFn: () => getProjectOnline(projectId),
    enabled: !!projectId && !paused,
  });

  const preWipe =
    !!wipeStartedAt &&
    !!q.data &&
    Date.parse(q.data.generated_at) < Date.parse(wipeStartedAt);
  const data = preWipe ? undefined : q.data;

  // Ages the caption while the tab sits open. The value is not read: the age
  // is computed from `generated_at` against the clock inside
  // `formatRelativeShort`, and what this hook is here for is the re-render
  // that makes it read that clock again. It runs only once there is a snapshot
  // to grow old.
  useNow(!!data, CAPTION_TICK_MS);

  // The link is the reason the band is a band and not a fourth stat card: it
  // is the way into the list this figure counts. It stays up over an error —
  // the players list has its own request and its own filter, and neither
  // depends on this one — and goes only while a wipe is emptying that list.
  const link = (
    <Link className={styles.link} to={`/projects/${projectId}/players?online=1`}>
      View online players
      <ChevronRight size={13} aria-hidden="true" />
    </Link>
  );

  if (paused === "wiping") {
    return (
      <div>
        <div className={styles.band}>
          <span className={styles.ico}>
            <Wifi size={17} aria-hidden="true" />
          </span>
          <div className={styles.main}>
            <span className={styles.label}>Players online</span>
            <span className={`${styles.value} ${styles.valueDim}`}>—</span>
          </div>
          <Split />
        </div>
        {/* Not a copy of the analytics row's "counting stops" note but the
            reason particular to this figure: a wipe closes the game API, so
            nobody can be connected, and presence is one of the things it
            deletes on the way through. */}
        <div className={styles.caption}>
          <Clock size={12} aria-hidden="true" />
          Presence · the game API is closed while a wipe runs
        </div>
      </div>
    );
  }

  if (!data && !q.isError) {
    // In flight, or held back by `paused: "unknown"`, or holding a count taken
    // before a wipe. All three are "we do not know yet".
    return (
      <div>
        <BandSkeleton />
        <div className={styles.caption}>
          <Clock size={12} aria-hidden="true" />
          {CAPTION_BASE}
        </div>
      </div>
    );
  }

  let footer: ReactNode;
  if (q.isError) {
    footer = (
      <div className={styles.errorStrip} role="alert">
        <AlertTriangle size={14} aria-hidden="true" />
        <span>
          Couldn&apos;t load who&apos;s online. The rest of this page still
          works.
        </span>
        <button
          type="button"
          className={styles.retry}
          onClick={() => q.refetch()}
        >
          Retry
        </button>
      </div>
    );
  } else {
    footer = (
      <div className={styles.caption}>
        <Clock size={12} aria-hidden="true" />
        {data ? captionFor(data.generated_at) : CAPTION_BASE}
      </div>
    );
  }

  return (
    <div>
      <div className={styles.band}>
        <span className={styles.ico}>
          <Wifi size={17} aria-hidden="true" />
        </span>
        <div className={styles.main}>
          <span className={styles.label}>Players online</span>
          {data ? (
            <span className={styles.value}>
              <span className={styles.liveDot} aria-hidden="true" />
              {NUM.format(data.real)}
            </span>
          ) : (
            <span className={`${styles.value} ${styles.valueDim}`}>—</span>
          )}
        </div>
        <Split counts={data} />
        <div className={styles.spacer} />
        {link}
      </div>
      {footer}
    </div>
  );
}

/**
 * What stands beside the headline figure: how many of the connections are the
 * simulation's, and what the two add up to.
 *
 * It is drawn in every state the band has, em-dashes included, and on a project
 * that runs no simulation at all. `Bots 0` is the useful answer «the simulation
 * is not running», and a breakdown that came and went with the data would move
 * the link beside it every time the band changed state.
 *
 * `Bots` is muted — it is context for the figure on the left, not a second
 * headline — while `Total` keeps the body colour: the sum is the load figure,
 * and that is the whole reason it stays on screen now that it is no longer the
 * headline. Without a payload both go faint, for the reason `.valueDim` exists:
 * an em-dash must not read as a value at a glance.
 */
function Split({ counts }: { counts?: Pick<ProjectOnline, "bots" | "total"> }) {
  const botsClass = counts
    ? `${styles.v} ${styles.bots}`
    : `${styles.v} ${styles.vDim}`;
  const totalClass = counts ? styles.v : `${styles.v} ${styles.vDim}`;
  return (
    <div className={styles.split}>
      <div>
        <div className={styles.k}>Bots</div>
        <div className={botsClass}>{counts ? NUM.format(counts.bots) : "—"}</div>
      </div>
      <div>
        <div className={styles.k}>Total</div>
        <div className={totalClass}>
          {counts ? NUM.format(counts.total) : "—"}
        </div>
      </div>
    </div>
  );
}

/**
 * Placeholder in the band's own geometry, so nothing below it moves.
 *
 * `aria-busy` and not `aria-hidden`, following the players list's LoadingTable
 * rather than StatCardSkeleton beside it. The bars hide themselves — the
 * Skeleton component sets `aria-hidden` on every one of them — so hiding the
 * container as well adds nothing except removing the band from the tree
 * entirely, which is how a region a screen reader was just told about vanishes
 * and comes back unannounced. Busy says the true thing: still here, still
 * loading.
 */
function BandSkeleton() {
  return (
    <div className={styles.band} aria-busy="true">
      <Skeleton width={34} height={34} radius={9} />
      <div className={styles.main}>
        <Skeleton width={86} height={11} />
        <Skeleton width={72} height={22} />
      </div>
      <div className={styles.split}>
        <div>
          <Skeleton width={30} height={11} />
          <Skeleton width={38} height={15} style={{ marginTop: 3 }} />
        </div>
        <div>
          <Skeleton width={32} height={11} />
          <Skeleton width={38} height={15} style={{ marginTop: 3 }} />
        </div>
      </div>
      <div className={styles.spacer} />
      <Skeleton width={132} height={13} />
    </div>
  );
}
